import React, { useEffect, useState } from 'react';
import styles from './PaymentPopup.module.css';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLang } from 'hooks/useLang';

import bePaidIcon from 'assets/images/bepaid-icon.png';
import eripIcon from 'assets/images/erip-icon.png';

import type { Course, PlanId, PricingPlan } from '../CoursePricing/CourseCard.types';
import RegularCard from '../CoursePricing/RegularCard';

type MethodPay = 'bepaid' | 'erip';
type MethodsItem = { key: MethodPay; method: string; description: string };

type Props = {
  planId: PlanId;
  i18nIndex?: number;
  price?: PricingPlan;
  onClose?: () => void;
};

const ICONS: Record<MethodPay, string> = {
  bepaid: bePaidIcon,
  erip: eripIcon,
} as const;

const PaymentPopup: React.FC<Props> = ({ planId, i18nIndex, price, onClose }) => {
  const { t } = useTranslation();
  const { lang } = useLang();

  const methods = t('paymentPopup.methods', { returnObjects: true }) as MethodsItem[];
  const courses = t('coursePricing.cards', { returnObjects: true }) as Course[];

  const idx = typeof i18nIndex === 'number' ? i18nIndex : courses.findIndex(c => c.id === planId);
  const course = idx >= 0 ? courses[idx] : undefined;

  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const toggle = (i: number) => setOpenIndex(prev => (prev === i ? null : i));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!course) return null;

  const handleOverlayMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div className={styles.popupOverlay} role="dialog" aria-modal="true" aria-labelledby="checkout-title" onMouseDown={handleOverlayMouseDown}>
      <section className={styles['payment-popup']} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles['popup-container']}>
          <div className={styles.header}>
            <h2 id="checkout-title" className={styles['sectionTitle']}>
              {t('paymentPopup.sectionTitle')}
            </h2>
          </div>
            <h3 className={styles['blockTitle']}>{t('paymentPopup.blockTitle.billing')}</h3>
          <div className={`${styles['paymentPopup-content']}`}>
              <div className={styles['leftPane']}>
              <div className={styles.fields}>
                  <div className={styles.field}>
                      <label className={styles.label} htmlFor="email">Your email*:</label>
                      <div className={styles.control}>
                        <input className={styles.input} type="email" id="email" name="email" placeholder="example@mail.com" autoComplete="email" />
                      </div>
                  </div>
                  <div className={styles.field}>
                      <label className={styles.label} htmlFor="promoCode">Promocode:</label>
                      <div className={styles.control}>
                        <input className={styles.input} type="text" id="promoCode" name="promoCode" placeholder="PROMOCODE" autoComplete="off" />
                      </div>
                  </div>
              </div>

              <h3 className={styles['blockTitle']}>{t('paymentPopup.blockTitle.methods')}</h3>

              <ul className={styles['methods']} role="radiogroup" aria-label={t('paymentPopup.blockTitle.methods')}>
                {methods.map((m, i) => {
                  const isOpen = openIndex === i;
                  return (
                    <li key={m.key}   className={`${styles['method-item']} ${isOpen ? styles.open : ''}`}>
                      <button type="button" role="radio" aria-checked={isOpen} className={`${styles['payment-method']} ${isOpen ? styles.active : ''}`} onClick={() => toggle(i)} aria-expanded={isOpen} aria-controls={`panel-${m.key}`} id={`trigger-${m.key}`}>
                        <img className={styles['method-icon']} src={ICONS[m.key]} alt="" aria-hidden="true" />
                        <p className={styles['method-name']}>{m.method}</p>
                        <span className={styles.chevron} aria-hidden="true" data-open={isOpen ? 'true' : 'false'} />
                      </button>

                      <div id={`panel-${m.key}`} role="region" aria-labelledby={`trigger-${m.key}`} className={`${styles['payment-details']} ${isOpen ? styles.open : ''}`}>
                        <p className={styles['method-description']}>{m.description}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className={styles['rightPane']}>
              <RegularCard course={course} i18nIndex={idx} price={price} />
            </div>
          </div>

          <p className={styles.legal}>
          <Trans i18nKey="paymentPopup.legal" 
                 components={{
                      termsLink: <Link to={`/${lang}/terms-and-conditions`} className={`${styles.legalLink} ${styles.terms}`} />,
                      refundLink: <Link to="/refund" className={`${styles.legalLink} ${styles.refund}`} />,
                      privacyLink: <Link to={`/${lang}/privacy-policy`} className={`${styles.legalLink} ${styles.privacy}`} />,
                  }}
          />
          </p>
        </div>
      </section>
    </div>
  );
};

export default PaymentPopup;